import { Plus } from "lucide-react";
import { formatPrice } from "./formatters";

export interface SwapCandidate {
  id: number;
  title: string;
  imageUrl: string | null;
  price: number | null;
  /** The variant the price belongs to — shown so the offer is exact. */
  variantId?: number | null;
  variantTitle?: string | null;
}

export interface SwapCurrentProduct {
  title: string;
  imageUrl: string | null;
  /** The variant on their current subscription line. */
  variantTitle?: string | null;
}

export interface ProductSwapOfferProps {
  /** What they're swapping FROM — one or more lines on the current subscription. */
  currentProducts: SwapCurrentProduct[];
  /** Current subscription price before the swap (for comparison copy). */
  currentPrice: number | null;
  /** The offered bundle (1..N products). */
  candidates: SwapCandidate[];
  /** REAL combined price of the offered products — what they pay after the swap. */
  comboValue: number | null;
  currencySymbol?: string;
  /** AI-authored intro line; falls back to the fixed pitch when absent. */
  message?: string;
}

function ProductThumb({
  title,
  imageUrl,
  variantTitle,
}: {
  title: string;
  imageUrl: string | null;
  /** Which variant the deal is for (e.g. "Travel Size 50ml") — honesty line. */
  variantTitle?: string | null;
}) {
  return (
    <div className="tw:flex tw:flex-col tw:items-center tw:gap-1 tw:w-20">
      <div className="tw:size-20 tw:rounded-lg tw:border tw:border-gray-200 tw:bg-white tw:overflow-hidden tw:flex tw:items-center tw:justify-center">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            className="tw:size-full tw:object-cover"
          />
        ) : (
          <span className="tw:text-[10px] tw:text-gray-400 tw:px-1 tw:text-center">
            {title}
          </span>
        )}
      </div>
      <span className="tw:text-[11px] tw:leading-tight tw:text-center tw:text-[#523D33] tw:line-clamp-2">
        {title}
      </span>
      {variantTitle && (
        <span className="tw:text-[10px] tw:leading-tight tw:text-center tw:text-gray-500 tw:line-clamp-1">
          {variantTitle}
        </span>
      )}
    </div>
  );
}

function ProductRow({
  products,
}: {
  products: Array<{
    title: string;
    imageUrl: string | null;
    variantTitle?: string | null;
    key: string;
  }>;
}) {
  return (
    <div className="tw:flex tw:flex-wrap tw:items-start tw:justify-center tw:gap-2">
      {products.map((p, i) => (
        <span key={p.key} className="tw:flex tw:items-center tw:gap-2">
          {i > 0 && (
            <Plus className="tw:size-4 tw:text-[#523D33]/60 tw:shrink-0" />
          )}
          <ProductThumb
            title={p.title}
            imageUrl={p.imageUrl}
            variantTitle={p.variantTitle}
          />
        </span>
      ))}
    </div>
  );
}

/**
 * Visual content for the "product swap" retention offer: a bundle of 1..N
 * products at a similar real price to what they pay now. Buttons are NOT
 * rendered here — in the chatbot the reply options are shown separately on
 * the user side.
 */
export function ProductSwapOffer({
  currentProducts,
  currentPrice,
  candidates,
  comboValue,
  currencySymbol = "$",
  message,
}: ProductSwapOfferProps) {
  const payPrice = comboValue ?? currentPrice;
  const isCheaper =
    currentPrice != null &&
    comboValue != null &&
    comboValue < currentPrice - 1e-9;

  const currentLabel =
    currentProducts.length === 1
      ? currentProducts[0]!.title
      : currentProducts.map((p) => p.title).join(" and ");

  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <p className="tw:text-sm tw:m-0">
        {message?.trim() ? (
          message
        ) : (
          <>
            How about we swap your {currentLabel} for{" "}
            {candidates.length === 1
              ? "this product"
              : `these ${candidates.length} products`}
            ?
          </>
        )}
      </p>

      {/*
        2+ current lines wrap awkwardly in one row on mobile, so stack:
        currents on row 1, offers on row 2. Single-current keeps the
        compact side-by-side layout.
      */}
      {currentProducts.length >= 2 ? (
        <div className="tw:flex tw:flex-col tw:items-center tw:gap-2">
          <ProductRow
            products={currentProducts.map((p, i) => ({
              ...p,
              key: `current-${i}-${p.title}`,
            }))}
          />
          <span
            className="tw:text-2xl tw:text-[#523D33]/50 tw:font-light tw:leading-none"
            aria-hidden
          >
            ↓
          </span>
          <ProductRow
            products={candidates.map((c, i) => ({
              title: c.title,
              imageUrl: c.imageUrl,
              variantTitle: c.variantTitle,
              key: `swap-${i}-${c.id}`,
            }))}
          />
        </div>
      ) : (
        <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-center tw:gap-2">
          <ProductRow
            products={currentProducts.map((p, i) => ({
              ...p,
              key: `current-${i}-${p.title}`,
            }))}
          />
          <span className="tw:text-2xl tw:text-[#523D33]/50 tw:font-light tw:px-1">
            →
          </span>
          <ProductRow
            products={candidates.map((c, i) => ({
              title: c.title,
              imageUrl: c.imageUrl,
              variantTitle: c.variantTitle,
              key: `swap-${i}-${c.id}`,
            }))}
          />
        </div>
      )}

      <div className="tw:flex tw:flex-col tw:gap-1 tw:rounded-lg tw:border tw:border-gray-200 tw:bg-white tw:px-3 tw:py-2.5 tw:text-sm tw:text-[#523D33]">
        {payPrice != null && (
          <p className="tw:m-0 tw:font-medium">
            {isCheaper ? (
              <>
                {currencySymbol}
                {formatPrice(payPrice)} per delivery
                {currentPrice != null ? (
                  <>
                    {" "}
                    (was {currencySymbol}
                    {formatPrice(currentPrice)})
                  </>
                ) : null}
                .
              </>
            ) : (
              <>
                {currencySymbol}
                {formatPrice(payPrice)} per delivery.
              </>
            )}
          </p>
        )}
        <p className="tw:m-0 tw:mt-1 tw:text-xs tw:text-gray-500">
          Try {candidates.length === 1 ? "it" : "them"} for a month. You can
          cancel in seconds if {candidates.length === 1 ? "it's" : "they're"}{" "}
          not for you.
        </p>
      </div>
    </div>
  );
}
